import { Button, fontStyles, Modal, ModalSection } from '@monash/portal-react';
import { useContext, useRef, useState } from 'react';
import c from './personalisation-modal.module.scss';
import classNames from 'classnames';
import {
  AccessibilityContext,
  fsUpdateDoc,
  ImpersonationContext,
} from '@monash/portal-frontend-common';
import { DataContext } from '@/components/providers/data-provider/DataProvider';
import { useSnackbar } from '@/components/providers/SnackbarProvider';

const QUESTION_ID = {
  EXCITEMENT: 'EXCITEMENT',
  STUDY_APPROACH: 'STUDY_APPROACH',
};

const ANSWER_ID = {
  LEADER: 'LEADER',
  PURPOSE_DRIVEN: 'PURPOSE_DRIVEN',
  INDIVIDUALIST: 'INDIVIDUALIST',
  EXPLORER: 'EXPLORER',
  JUGGLER: 'JUGGLER',
  COMMITTED: 'COMMITTED',
  INTL_NEWCOMER: 'INTL_NEWCOMER',
  STRUGGLER: 'STRUGGLER',
};

const PERSONALISATION_QUESTIONS = [
  {
    id: QUESTION_ID.EXCITEMENT,
    text: 'What most excites you about university right now?',
    maxAnswers: 3,
    answers: [
      {
        id: ANSWER_ID.LEADER,
        text: '📣 Getting involved in campus life and meeting new people.',
      },
      {
        id: ANSWER_ID.PURPOSE_DRIVEN,
        text: '✨ Learning about topics that are meaningful to me.',
      },
      {
        id: ANSWER_ID.INDIVIDUALIST,
        text: '🌈 Having the freedom to be myself and do my own thing.',
      },
      {
        id: ANSWER_ID.EXPLORER,
        text: '🗺️ Exploring the different clubs, events and subjects on offer.',
      },
      {
        id: ANSWER_ID.JUGGLER,
        text: '⚖️ Mastering the balance between classes and everything else',
      },
      {
        id: ANSWER_ID.COMMITTED,
        text: '📖 Setting myself up for a great start with my studies.',
      },
      {
        id: ANSWER_ID.INTL_NEWCOMER,
        text: '🌏 Settling into a new city and discovering its culture.',
      },
      {
        id: ANSWER_ID.STRUGGLER,
        text: '⚓ Settling into university life and finding my rhythm.',
      },
    ],
  },
  {
    id: QUESTION_ID.STUDY_APPROACH,
    text: 'Which matches your study approach?',
    maxAnswers: 3,
    answers: [
      {
        id: ANSWER_ID.LEADER,
        text: '📣 I like to organise study groups and help others stay on track.',
      },
      {
        id: ANSWER_ID.PURPOSE_DRIVEN,
        text: '🌍 I focus on how my studies will help me solve real-world problems.',
      },
      {
        id: ANSWER_ID.INDIVIDUALIST,
        text: '🦄 I prefer unconventional methods and following my own unique rhythm.',
      },
      {
        id: ANSWER_ID.EXPLORER,
        text: '🔍 I love connecting ideas across different subjects and exploring new tools.',
      },
      {
        id: ANSWER_ID.JUGGLER,
        text: "⏱️ I'm all about efficiency - fitting my studies around a busy schedule.",
      },
      {
        id: ANSWER_ID.COMMITTED,
        text: "🎓 I'm highly disciplined and focused on getting the best results.",
      },
      {
        id: ANSWER_ID.INTL_NEWCOMER,
        text: "🗺️ I'm learning to navigate a new academic system and culture.",
      },
      {
        id: ANSWER_ID.STRUGGLER,
        text: "⚓ I'm currently looking for a better routine to help me manage the workload.",
      },
    ],
  },
];

const PersonalisationModal = ({ open, onClose }) => {
  const { currentUser } = useContext(ImpersonationContext);
  const { resetAppLiveMsgs } = useContext(AccessibilityContext);
  const { setPortalPreferences } = useContext(DataContext);
  const userDocPath = `users/${currentUser.uid}`;

  const { addSnackbar } = useSnackbar();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [questionResponses, setQuestionResponses] = useState({});
  const questionsRef = useRef([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questionCount = PERSONALISATION_QUESTIONS.length;
  const showBackButton = currentIndex > 0;
  const showSubmitButton = currentIndex === questionCount - 1;
  const questionResponsesValues = Object.values(questionResponses);
  const canSubmit =
    questionResponsesValues.length === questionCount &&
    questionResponsesValues.every((response) => response?.length > 0);

  const scrollToQuestion = (index) => {
    const target = questionsRef.current?.[index];
    target.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAnswerSelect = ({ questionId, answerId, maxAnswers }) => {
    setQuestionResponses((prev) => {
      const prevAnswersToQuestion = prev[questionId];
      const shouldSelect = !prevAnswersToQuestion?.includes(answerId);

      let newAnswers;

      if (shouldSelect) {
        const hasNoAnswer = !prevAnswersToQuestion?.length;
        const hasReachedMax = prevAnswersToQuestion?.length === maxAnswers;
        if (hasNoAnswer) {
          newAnswers = [answerId];
        } else if (hasReachedMax) {
          newAnswers = [...prevAnswersToQuestion.slice(1), answerId];
        } else {
          newAnswers = [...prevAnswersToQuestion, answerId];
        }
      } else {
        newAnswers = prevAnswersToQuestion.filter((ans) => ans !== answerId);
      }

      return {
        ...prev,
        [questionId]: newAnswers,
      };
    });
  };

  const handleBack = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex > 0) {
        const targetIndex = prevIndex - 1;
        scrollToQuestion(targetIndex);
        return targetIndex;
      }

      return prevIndex;
    });
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex < questionCount - 1) {
        const targetIndex = prevIndex + 1;
        scrollToQuestion(targetIndex);
        return targetIndex;
      }

      return prevIndex;
    });
  };

  const handleClose = () => {
    onClose();
    setCurrentIndex(0);
    setQuestionResponses({});
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const now = Date.now();
    setIsSubmitting(true);
    try {
      await fsUpdateDoc(userDocPath, {
        'preferences.linksPersonalisation.questionResponses': questionResponses,
        'preferences.linksPersonalisation.responsesUpdatedAt': now,
      });

      setPortalPreferences((prev) => ({
        ...prev,
        linksPersonalisation: {
          ...(prev.linksPersonalisation ?? {}),
          questionResponses,
          responsesUpdatedAt: now,
        },
      }));

      handleClose();
    } catch (error) {
      resetAppLiveMsgs();
      addSnackbar({
        message:
          'We are unable to save your personalisation results right now - try again later',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} className={c.modal}>
      <ModalSection noPadding headerClassName={c.modalHeader}>
        <div className={c.personalisationContainer}>
          <div className={c.questions}>
            {PERSONALISATION_QUESTIONS.map((question, i) => {
              const {
                id: questionId,
                text: questionText,
                maxAnswers,
              } = question;

              return (
                <div
                  key={questionId}
                  ref={(node) => {
                    questionsRef.current[i] = node;
                  }}
                  className={c.questionContainer}
                >
                  <div className={c.questionHeader}>
                    <div
                      className={classNames(fontStyles.heading, fontStyles.md)}
                    >
                      {questionText}
                    </div>
                    <div>{`Select up to ${maxAnswers} answers`}</div>
                  </div>
                  <div className={c.answers}>
                    {question.answers?.map((answer) => {
                      const { id: answerId, text: answerText } = answer;

                      return (
                        <button
                          key={answerId}
                          type="button"
                          className={classNames(c.answerButton, {
                            [c.selected]:
                              questionResponses[questionId]?.includes(answerId),
                          })}
                          onClick={() =>
                            handleAnswerSelect({
                              questionId,
                              answerId,
                              maxAnswers,
                            })
                          }
                        >
                          {answerText}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className={c.controls}>
            <div className={c.dots} inert>
              {Array.from({ length: questionCount }, (_, i) => (
                <div
                  key={i}
                  className={classNames(c.dot, {
                    [c.current]: currentIndex === i,
                  })}
                />
              ))}
            </div>
            <div className={c.buttons}>
              {showBackButton && (
                <Button onClick={handleBack} variant="secondary">
                  Back
                </Button>
              )}
              {showSubmitButton ? (
                <Button
                  onClick={handleSubmit}
                  variant="primary"
                  disabled={!canSubmit}
                  loading={isSubmitting}
                >
                  Submit
                </Button>
              ) : (
                <Button onClick={handleNext} variant="primary">
                  Next
                </Button>
              )}
            </div>
          </div>
        </div>
      </ModalSection>
    </Modal>
  );
};

export default PersonalisationModal;
